import { Injectable, inject } from '@angular/core';
import { HttpClientService } from '@core/services/http-client.service';
import { Router } from '@angular/router';
import { VisionBooking } from '@core/models/VisionBooking';
import { FacilitySessionDto, FacilitySessionDetailDto, SelectedFacilityDto, VisionBookingDto } from '@core/models/dto/VisionBookingModelDto';
import { VisionService } from '@booking/vision/services/vision.service';
import { JsonFormatterService } from '@booking/common/json-formatter.service';
import { tap } from 'rxjs';
import { MessageService } from '@core/services/message.service';
import { MessageType } from '@core/enums/MessageType.enum';

@Injectable({
  providedIn: 'root'
})
export class BookResearchVisionService{
  private visionService = inject(VisionService);
  private httpClientService = inject(HttpClientService);
  private router = inject(Router);
  private jsonFormmater = inject(JsonFormatterService);
  private messageService = inject(MessageService);

  private mapObjectBeforeSending(visionBooking: VisionBooking): VisionBookingDto {
    const controls = visionBooking.controls;
    const rawValue = visionBooking.getRawValue();

    const facilityRespondentMarketDto = rawValue['sessions'].map((item: any) => {
      const selectedFacilityDto: SelectedFacilityDto = {
        name: item.facility.address ? item.facility.locationName : item.facility.facilityName,
        address: item.facility.address,
        country: this.getCountry(item.facility.country),
        city: this.getCity(item.facility.city, item.facility.country)
      };

      const facilitySessionsDetailDto: FacilitySessionDetailDto = {
        facilityServiceTypeId: rawValue['serviceTypeId'],
        locationListed: !item.facility.locationNotListed,
        selectedFacility: selectedFacilityDto,
        multiLanguageAudioOptionId: item.language
      };

      const facilitySession: FacilitySessionDto = {
        id: item.sessionId,
        date: item.startDate,
        hoursStart: item.hoursStart === 'TBC' ? null : +item.hoursStart,
        hoursEnd: item.hoursEnd === 'TBC' ? null : +item.hoursEnd,
        minutesStart: item.minutesStart === 'TBC' ? null : +item.minutesStart,
        minutesEnd: item.minutesEnd === 'TBC' ? null : +item.minutesEnd,
        referenceNumber: item.referenceNumber,
        facilitySessionsDetail: facilitySessionsDetailDto,
        advancedPrivacy: item.facility.advancedPrivacy
      };

      return {
        id: item.id,
        ...(rawValue['humanTranscript']['transcript'] && {
          humanTranscript: {
            languageOptionId: rawValue['humanTranscript']['languageOptionId'],
            deliveryOptionId: rawValue['humanTranscript']['deliveryOptionId'],
          },
        }),
        facilitySession: [facilitySession],
      };
    });

    const res = {
      projectTopic: controls['projectTopic'].value,
      coordinator: controls['coordinator'].getRawValue(),
      contactDetail: controls['contactDetail'].getRawValue(),
      usabilityStudy: controls['usabilityStudy'].value,
      researchTypeId: 1,
      interviewTypeId: controls['interviewTypeId'].value,
      billingDetail: controls['billingDetail'].getRawValue(),
      facilityRespondentMarket: facilityRespondentMarketDto
    };

    if(res.billingDetail.poNumberNotRequired)delete res.billingDetail.poNumber;
    if(res.billingDetail.jobNumberNotRequired)delete res.billingDetail.jobNumber;

    return this.jsonFormmater.cleanData(res);
  }

  private getCountry(id: number): string | undefined {
    return this.visionService.getCountryById(id);
  }

  private getCity(cityId: number, countryId: number): string | undefined {
    return this.visionService.getCityById(cityId, countryId);
  }

  public save$(bookFacilityResearchRequest: VisionBooking) {
    const mappedObj = this.mapObjectBeforeSending(bookFacilityResearchRequest);
    return this.httpClientService.post(mappedObj, 'BookResearch/facility').pipe(
      tap(() =>this.router.navigate(['/thank-you']))
    );
  }

  public edit(bookFacilityResearchRequest: VisionBooking, researchId: number) {
    const mappedObj = this.mapObjectBeforeSending(bookFacilityResearchRequest);
    this.httpClientService.put(mappedObj, `BookingDashboard/FacilityResearch/Edit/${researchId}`)
    .pipe(tap(() => this.messageService.loadMessage('Booking changed.', MessageType.SAVED)))
    .subscribe(() => this.onSucess());
  }

  onSucess() {
    this.router.navigate(['/profile/dashboard']);
  }
}
